import { Link } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import { Play, Plus, Info } from "lucide-react";

const VARIANTS = {
  poster: "aspect-[2/3]",
  landscape: "aspect-video",
  reel: "aspect-[9/16]",
};

export default function ContentCard({
  item,
  tenantSlug,
  variant = "landscape",
  themeColor = "#7C3AED",
  accentColor = "#F59E0B",
  showCreator = false,
  showProgress = false,
  progressPercent = 0,
}) {
  const aspect = VARIANTS[variant] || VARIANTS.landscape;
  const fallbackBg = `linear-gradient(135deg, ${themeColor}33, #0a0a0a 70%)`;
  const isReel = item.kind === "reel";
  const { sp } = useStore();
  const detailHref = isReel ? sp(`/r/${item.slug}`) : sp(`/title/${item.slug}`);
  const watchHref = isReel ? sp(`/r/${item.slug}`) : sp(`/watch/${item.slug}`);

  // Metadata strip line
  const metaParts = [];
  if (item.release_year) metaParts.push(item.release_year);
  if (item.duration_minutes) metaParts.push(`${item.duration_minutes} min`);
  if (item.age_rating) metaParts.push(item.age_rating);
  if ((item.genres || [])[0]) metaParts.push(item.genres[0]);

  const effectiveProgress = showProgress
    ? progressPercent
    : (typeof item._progress_pct === "number" ? item._progress_pct : 0);
  const hasProgress = effectiveProgress > 0;

  return (
    <Link
      to={detailHref}
      className="group relative block flex-shrink-0 transition-transform duration-200 hover:scale-[1.04] hover:-translate-y-0.5 hover:z-10"
      data-testid={`content-card-${item.slug}`}
    >
      <div
        className={`relative ${aspect} w-full overflow-hidden bg-[#121212] border border-white/10 group-hover:border-white/35 transition-all duration-200 group-hover:shadow-[0_18px_50px_-15px_rgba(0,0,0,0.85)]`}
        style={{ background: item.thumbnail_url ? undefined : fallbackBg }}
      >
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt={item.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : null}

        {/* Bottom gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-90" />

        {/* Top-left badges */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {item.kind !== "movie" && (
            <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] bg-black/70 border border-white/15">
              {item.kind}
            </span>
          )}
        </div>

        {/* Progress bar overlay (continue-watching) */}
        {hasProgress && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
            <div
              className="h-full"
              style={{ width: `${Math.min(100, Math.max(0, effectiveProgress))}%`, backgroundColor: accentColor }}
            />
          </div>
        )}

        {/* Hover overlay: quick-action buttons + metadata */}
        <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-3" data-testid={`content-card-hover-${item.slug}`}>
          <div className="flex items-center gap-2 mb-2">
            <span
              className="grid place-items-center w-9 h-9 rounded-full transition-transform group-hover:scale-105"
              style={{ backgroundColor: accentColor }}
              title="Play"
              onClick={(e) => { e.preventDefault(); window.location.href = watchHref; }}
              data-testid={`card-play-${item.slug}`}
            >
              <Play className="w-4 h-4 text-black fill-black" />
            </span>
            <span
              className="grid place-items-center w-9 h-9 rounded-full border border-white/40 hover:border-white text-white/90 hover:text-white"
              title="Add to my list"
              data-testid={`card-add-${item.slug}`}
            >
              <Plus className="w-4 h-4" />
            </span>
            <span
              className="grid place-items-center w-9 h-9 rounded-full border border-white/40 hover:border-white text-white/90 hover:text-white"
              title="More info"
              data-testid={`card-info-${item.slug}`}
            >
              <Info className="w-4 h-4" />
            </span>
          </div>
          {metaParts.length > 0 && (
            <div className="flex flex-wrap gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.25em] text-white/70">
              {metaParts.slice(0, 4).map((p, i) => (
                <span key={i}>{p}</span>
              ))}
            </div>
          )}
        </div>

        {/* Default title strip (always visible) */}
        <div className="absolute bottom-0 inset-x-0 p-3 group-hover:opacity-0 transition-opacity duration-200">
          <h3 className="font-display font-bold text-sm md:text-base tracking-tight leading-tight line-clamp-2">
            {item.title}
          </h3>
          {showCreator && item.creator_name ? (
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{item.creator_name}</div>
          ) : (
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1 flex items-center gap-2">
              {item.release_year && <span>{item.release_year}</span>}
              {item.age_rating && <span className="px-1 border border-white/20">{item.age_rating}</span>}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
